/**
 * HypotheticalPedigreePage
 * ------------------------
 * Pick a sire and a dam from the herd. See the 5-generation pedigree of
 * their hypothetical foal, the coefficient of inbreeding (Wright's path
 * method, lower bound), and any linebreeding (ancestors appearing more
 * than once).
 *
 * Reuses the PedigreeTable component for the chart rendering — the backend
 * returns display data in the same shape /pedigree/display does.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PrintIcon from '@mui/icons-material/Print';
import ShareIcon from '@mui/icons-material/Share';

import PedigreeTable from '../components/PedigreeTable';
import CoiCard from '../components/CoiCard';
import LinebreedingCard from '../components/LinebreedingCard';
import SharePedigreeDialog from '../components/SharePedigreeDialog';
import PedigreePrintout from '../components/PedigreePrintout';
import {
  PedigreePrintStyles,
  PrintWatermark,
  pedigreePrintSx,
} from '../components/PedigreePrintLayer';
import { horsesAPI, breedingAPI } from '../services/api';
import { createPrefixFilter } from '../utils/autocompleteFilters';

function getHorseOptionLabel(h) {
  if (!h) return '';
  const reg = h.registered_name?.trim();
  const barn = h.name?.trim();
  return reg && barn ? `${reg} (${barn})` : reg || barn || `Horse #${h.id}`;
}

function HorsePicker({ label, horses, value, onChange, disabled }) {
  return (
    <Autocomplete
      options={horses}
      value={value}
      onChange={(_e, v) => onChange(v)}
      disabled={disabled}
      getOptionLabel={getHorseOptionLabel}
      filterOptions={createPrefixFilter(getHorseOptionLabel)}
      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
      renderInput={(params) => (
        <TextField size="small" {...params} label={label} variant="outlined" fullWidth />
      )}
      renderOption={(props, h) => (
        <li {...props} key={h.id}>
          <Box>
            <Typography variant="body1">
              {h.registered_name || h.name || `Horse #${h.id}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {[h.type, h.breed, h.date_of_birth?.slice(0, 4)].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        </li>
      )}
    />
  );
}

export default function HypotheticalPedigreePage() {
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sire, setSire] = useState(null);
  const [dam, setDam] = useState(null);
  const [result, setResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Load the herd for the autocomplete pickers
  useEffect(() => {
    let cancelled = false;
    horsesAPI
      .getAll()
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : res.data?.horses || [];
        setHorses(list);
      })
      .catch((err) => setError(err.response?.data?.error || `Failed to load horses: ${err.message}`))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Partition horses by type for the two pickers, sorted alphabetically by
  // registered_name (falling back to barn name when registered_name is null).
  const sortHorses = (list) =>
    [...list].sort((a, b) => {
      const an = (a.registered_name || a.name || '').toLowerCase();
      const bn = (b.registered_name || b.name || '').toLowerCase();
      return an.localeCompare(bn);
    });

  const candidateSires = useMemo(
    () => sortHorses(horses.filter((h) => /stallion|colt|gelding/i.test(h.type || ''))),
    [horses],
  );
  const candidateDams = useMemo(
    () => sortHorses(horses.filter((h) => /mare|filly/i.test(h.type || ''))),
    [horses],
  );

  // Fetch the hypothetical pedigree whenever both parents are selected
  useEffect(() => {
    if (!sire || !dam) {
      setResult(null);
      return;
    }
    let cancelled = false;
    setAnalyzing(true);
    setError(null);
    breedingAPI
      .getHypothetical(sire.id, dam.id)
      .then((res) => {
        if (!cancelled) setResult(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err.response?.data?.error || err.message;
        const hint = err.response?.data?.hint;
        setError(hint ? `${msg} — ${hint}` : msg);
        setResult(null);
      })
      .finally(() => {
        if (!cancelled) setAnalyzing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sire, dam]);

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 3,
        ...(expanded && result
          ? {
              display: 'flex',
              flexDirection: 'column',
              minHeight: 'calc(100vh - 112px)',
            }
          : {}),
        ...pedigreePrintSx,
      }}
    >
      <PedigreePrintStyles hideAppChrome />
      <PrintWatermark />

      {/* What actually gets printed — the same component the shared-link page
          shows, so both produce a byte-identical document. Everything below is
          the interactive screen UI and is suppressed on paper. */}
      {result && (
        <Box className="print-only" sx={{ display: 'none' }}>
          <PedigreePrintout data={result} />
        </Box>
      )}

      <Typography variant="h4" className="no-print" gutterBottom>
        Hypothetical Pedigree
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }} className="no-print">
        Pick a sire and a dam to see the 5-generation pedigree of their potential
        foal, the coefficient of inbreeding, and any common ancestors.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }} className="no-print">
        <Grid item xs={12} md={6}>
          <HorsePicker
            label="Sire (Stallion)"
            horses={candidateSires}
            value={sire}
            onChange={setSire}
            disabled={loading}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <HorsePicker
            label="Dam (Mare)"
            horses={candidateDams}
            value={dam}
            onChange={setDam}
            disabled={loading}
          />
        </Grid>
      </Grid>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {analyzing && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Building hypothetical pedigree…</Typography>
        </Stack>
      )}

      {result && (
        <Grid
          container
          spacing={2}
          className="no-print"
          sx={expanded ? { flex: 1, minHeight: 0 } : undefined}
        >
          {!expanded && (
            <Grid item xs={12} md={4}>
              {/* On paper this stack turns into a row — see pedigreePrintSx. */}
              <Stack spacing={2} className="pedigree-analysis">
                <CoiCard coi={result.coi} />
                <LinebreedingCard linebreeding={result.linebreeding} />
              </Stack>
            </Grid>
          )}
          <Grid
            item
            xs={12}
            md={expanded ? 12 : 8}
            className="pedigree-chart"
            sx={
              expanded
                ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }
                : undefined
            }
          >
            <Card
              variant="outlined"
              sx={
                expanded
                  ? { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }
                  : undefined
              }
            >
              <CardContent
                sx={
                  expanded
                    ? {
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        overflow: 'hidden',
                      }
                    : undefined
                }
              >
                <Stack direction="row" alignItems="center" sx={{ mb: 1 }} className="no-print">
                  <Box sx={{ flexGrow: 1 }} />
                  <Tooltip title="Share a public link">
                    <IconButton size="small" onClick={() => setShareOpen(true)}>
                      <ShareIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Print / save as PDF">
                    <IconButton size="small" onClick={() => window.print()}>
                      <PrintIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={expanded ? 'Show analysis cards' : 'Expand pedigree'}>
                    <IconButton size="small" onClick={() => setExpanded((v) => !v)}>
                      {expanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Box sx={expanded ? { flex: 1, minHeight: 0, overflow: 'auto' } : undefined}>
                  <PedigreeTable data={result.display} fillHeight={expanded} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <SharePedigreeDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        sire={sire}
        dam={dam}
      />
    </Container>
  );
}